import { Divider } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@material-ui/lab/Autocomplete';
import axios from 'axios';
import { debounce } from 'debounce';
import { observer } from 'mobx-react-lite';
import * as qs from 'qs';
import React, { useContext, useEffect, useState } from 'react';
import { Link, LinkProps, RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import AwsRegions from '../../common/AwsRegions';
import LoadingIndicator from '../../common/LoadingIndicator';
import NoResultsText from '../../common/NoResultsText';
import MobxContext from '../../mobx/MobxContext';
import FaroParamSetsTable from './FaroParamSetsTable';

const CancelToken = axios.CancelToken;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    searchTextField: {
      backgroundColor: theme.palette.background.paper,
      [theme.breakpoints.up('md')]: {
        marginLeft: 16,
        maxWidth: 300
      }
    },
    toolbarNormalStyle: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'space-between'
      }
    },
    toolbarLandingPageStyle: {
      marginBottom: 16
    },
    toolbarActions: {
      marginBottom: 8,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        marginBottom: 0
      }
    },
    environmentAutocomplete: {
      backgroundColor: theme.palette.background.paper,
      marginBottom: 8,
      minWidth: 200,
      [theme.breakpoints.up('md')]: {
        marginRight: 16,
        marginBottom: 0
      }
    },
    regionAutocomplete: {
      backgroundColor: theme.palette.background.paper,
      minWidth: 200
    },
    recentlyViewedParamSetsSection: {
      marginBottom: 24
    },
    chip: {
      color: theme.palette.text.secondary,
      margin: '0 8px 8px 0'
    },
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      paddingTop: 8
    },
    landingPageSearchContainer: {
      maxWidth: 600,
      margin: '64px auto auto auto'
    },
    landingPageTitle: { 
      marginBottom: 24 
    },
    quickLinksContainer: { 
      marginTop: 40 
    }
  })
);

const AdapterLink = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
  <Link innerRef={ref as any} {...props} />
));

const windsorApi = new WindsorApi();

function FaroParamSetListPage(props: RouteComponentProps<any>) {
  const { history, location } = props;
  const classes = useStyles({});
  const { environmentName, region, search = '' } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const [paramSets, setParamSets] = useState(null);
  const [page, setPage] = useState(0);
  const [localSearch, setLocalSearch] = useState(search);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const { environmentStore, preferenceStore } = useContext(MobxContext);

  const debouncedOnLocalSearchChange = debounce(setLocalSearch, 200);

  useEffect(() => {
    // If both environment and region are cleared this is a new search
    if (localSearch && !environmentName && !region) {
      setLocalSearch('');
    }
  }, [environmentName, region, localSearch]);

  useEffect(() => {
    setSearchInProgress(false);
    setParamSets(null);
    setPage(0);

    if (environmentStore.environments && environmentName && region) {
      setSearchInProgress(true);

      preferenceStore.addRecentlyViewedEnvironment(environmentName, region);

      const cancelTokenSource = CancelToken.source();
      const environment = environmentStore.getEnvironmentByName(environmentName);
      windsorApi.getFaroParameterSets(environment.AccountID, region, search, { cancelToken: cancelTokenSource.token }).then(res => {
        setParamSets(res.Items.map(item => ({
          name: item.Name,
          version: item.Version,
          lastModified: item.Version
        })));
        setSearchInProgress(false);
      }).catch(error => {
        if (!axios.isCancel(error)) {
          setSearchInProgress(false);
          console.error(`Failed to load parameter sets:\n${error}`);
        }
      });

      return () => cancelTokenSource.cancel();
    }
  }, [environmentStore, preferenceStore, environmentStore.environments, environmentName, region, search]);

  const updateQueryParams = (newParams) => {
    let queryString = '';
    Object.keys(newParams).filter(key => newParams[key] !== undefined && newParams[key] !== '').forEach(key => {
      if (queryString.length) {
        queryString += '&';
      }
      queryString += `${key}=${newParams[key]}`;
    });
    if (queryString.length) {
      queryString = `?${queryString}`;
    }
    history.push({ search: queryString })
  };

  const onSearchInputKeyPress = (event) => {
    // Only handle enter key type
    if (event.which === 13) {
      // Clear scheduled execution
      debouncedOnLocalSearchChange.clear();
      setLocalSearch(event.target.value);
      updateQueryParams({ environmentName, region, search: event.target.value });
    }
  }

  function onPageChange(newPage: number) {
    setPage(newPage);
  }

  const onRegionChange = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      const newRegion = value.code;
      updateQueryParams({ environmentName, region: newRegion, search: environmentName && newRegion ? localSearch : search });
    }
  };

  const onEnvironmentChange = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      const newEnvironment = value.EnvironmentName;
      updateQueryParams({ environmentName: newEnvironment, region, search: newEnvironment && region ? localSearch : search });
    }
  };

  const environment = environmentStore.environments && environmentName ? environmentStore.getEnvironmentByName(environmentName) : null;

  const useLandingPageFormat = !paramSets && !searchInProgress;

  return environmentStore.environments ? (
    <div className={classes.root}>
      <div>
        {!useLandingPageFormat && <div>
          <div className={classes.header}>
            <Typography variant="h5" gutterBottom>Parameter Sets</Typography>
            <Button
              color="primary"
              variant="contained"
              component={AdapterLink}
              to={`/faro/parameters/new${environmentName && region ? `?defaultEnvironmentName=${environmentName}&defaultRegion=${region}` : ''}`}
            >
              New Parameter Set
            </Button>
          </div>
          <Divider className={classes.divider} light />
        </div>}
        {environmentStore.environments && <div className={useLandingPageFormat ? classes.landingPageSearchContainer : null}>
          {useLandingPageFormat && <Typography variant="h5" className={classes.landingPageTitle}>Search for Parameter Sets</Typography>}
          <div className={useLandingPageFormat ? classes.toolbarLandingPageStyle : classes.toolbarNormalStyle}>
            <div className={classes.toolbarActions}>
              <Autocomplete
                size={useLandingPageFormat ? 'medium' : 'small'}
                fullWidth
                className={classes.environmentAutocomplete}
                options={environmentStore.environments}
                value={environment}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.Alias}
                onChange={onEnvironmentChange}
                getOptionSelected={(option, value) => value ? option.EnvironmentName === value.EnvironmentName : false}
                renderOption={(option, { selected }) => (
                  <div>
                    <Typography display="block">{option.Alias}</Typography>
                    <Typography variant="caption" display="block">{option.AccountID}</Typography>
                  </div>
                )}
                renderInput={(params) => <TextField
                  {...params}
                  placeholder="Environment"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
              <Autocomplete
                size={useLandingPageFormat ? 'medium' : 'small'}
                fullWidth
                className={classes.regionAutocomplete}
                options={AwsRegions || null}
                value={region || null}
                onChange={onRegionChange}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.code}
                getOptionSelected={(option, value) => value ? option.code === value : false}
                renderInput={(params) => <TextField
                  {...params}
                  placeholder="Region"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
            </div>
            {!useLandingPageFormat && <TextField
              id="search"
              size="small"
              defaultValue={localSearch}
              className={classes.searchTextField}
              fullWidth
              autoComplete="off"
              margin="none"
              variant="outlined"
              placeholder="Search by name (optional)"
              inputProps={{ 'aria-label': 'bare', spellCheck: false }}
              onKeyPress={onSearchInputKeyPress}
              onChange={event => debouncedOnLocalSearchChange(event.target.value)}
            />}
          </div>
          <div>
            {!paramSets && !searchInProgress && <div className={classes.quickLinksContainer}>
              {preferenceStore.recentlyViewedParamSets.length > 0 && <div className={classes.recentlyViewedParamSetsSection}>
                <Typography variant="subtitle2" color="textSecondary">RECENTLY VIEWED PARAMETER SETS</Typography>
                <div className={classes.chipContainer}>
                  {preferenceStore.recentlyViewedParamSets.map(recent => <Chip
                    key={`${recent.accountId}:${recent.region}:${recent.name}`}
                    className={classes.chip}
                    size="small"
                    component={AdapterLink}
                    to={`/faro/parameters/${recent.accountId}/${recent.region}/${recent.name}`}
                    clickable
                    variant="outlined"
                    label={recent.name}
                  />)}
                </div>
              </div>}
              {preferenceStore.recentlyViewedEnvironments.length > 0 && <div>
                <Typography variant="subtitle2" color="textSecondary">RECENTLY VIEWED ENVIRONMENTS</Typography>
                <div className={classes.chipContainer}>
                  {preferenceStore.recentlyViewedEnvironments.filter(recent => !!environmentStore.getEnvironmentByName(recent.environmentName)).map(recent => <Chip
                    key={`${recent.environmentName}:${recent.region}`}
                    className={classes.chip}
                    size="small"
                    component={AdapterLink}
                    to={`/faro/parameters?environmentName=${recent.environmentName}&region=${recent.region}${localSearch ? `&search=${localSearch}` : ''}`}
                    clickable
                    variant="outlined"
                    label={`${environmentStore.getEnvironmentByName(recent.environmentName).Alias} - ${recent.region}`}
                  />)}
                </div>
              </div>}
            </div>}
          </div>
          {environment && paramSets && paramSets.length > 0 && <div>
            <Typography color="textSecondary">{paramSets.length} parameter set{paramSets.length !== 1 ? 's' : ''} found</Typography>
            <Paper>
              <FaroParamSetsTable
                paramSets={paramSets}
                accountId={environment.AccountID}
                region={region}
                page={page}
                onPageChange={onPageChange}
              />
            </Paper>
          </div>}
          {paramSets && search && paramSets.length === 0 && <NoResultsText>No parameter sets matching search</NoResultsText>}
          {paramSets && !search && paramSets.length === 0 && <NoResultsText>No parameters sets in the selected environment</NoResultsText>}
          <LoadingIndicator loading={searchInProgress} />
        </div>}
      </div>
    </div>
  ) : <LoadingIndicator loading={true} />;
};

export default withRouter(observer(FaroParamSetListPage));
